import { LOCALE_LABELS, LOCALES, RADIUS } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import type { AdminUser, AdminUserUpdate } from "~/types/users-admin";

interface UserFormProps {
  initial: AdminUser;
  isSelf: boolean;
  busy: boolean;
  errorKey: string | null;
  onSubmit: (update: AdminUserUpdate) => void;
  actions?: ReactNode;
}

/** Safe account controls plus a read-only view of identity and commerce-owned data. */
export function UserForm({
  initial,
  isSelf,
  busy,
  errorKey,
  onSubmit,
  actions,
}: UserFormProps) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();

  const [displayName, setDisplayName] = useState(initial.display_name ?? "");
  const [locale, setLocale] = useState(initial.locale);
  const [isActive, setIsActive] = useState(initial.is_active);
  const [hasMembershipOverride, setHasMembershipOverride] = useState(
    initial.has_membership_override,
  );

  const formatTimestamp = (value: string | null) =>
    value
      ? formatDate(value, {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : t("common.never");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const update: AdminUserUpdate = {};
    const normalizedDisplayName = displayName.trim() || null;
    if (normalizedDisplayName !== initial.display_name) {
      update.display_name = normalizedDisplayName;
    }
    if (locale !== initial.locale) update.locale = locale;
    if (isActive !== initial.is_active) update.is_active = isActive;
    if (hasMembershipOverride !== initial.has_membership_override) {
      update.has_membership_override = hasMembershipOverride;
    }
    onSubmit(update);
  };

  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 7fr) minmax(300px, 5fr)",
        },
        gap: { xs: 4, md: 6 },
        alignItems: "start",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          label={t("userEditor.fields.email")}
          value={initial.email}
          disabled
          fullWidth
          slotProps={{ htmlInput: { "data-testid": "user.email" } }}
        />

        <TextField
          label={t("userEditor.fields.displayName")}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          fullWidth
          slotProps={{
            htmlInput: { "data-testid": "user.display-name", maxLength: 255 },
          }}
        />

        <TextField
          select
          label={t("userEditor.fields.locale")}
          value={locale}
          onChange={(event) => setLocale(event.target.value)}
          slotProps={{ htmlInput: { "data-testid": "user.locale" } }}
          sx={{ maxWidth: 280 }}
        >
          {LOCALES.map((option) => (
            <MenuItem key={option} value={option}>
              {LOCALE_LABELS[option]}
            </MenuItem>
          ))}
        </TextField>

        <Paper
          variant="outlined"
          sx={{
            padding: 2.5,
            borderRadius: `${RADIUS.lg}px`,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
                disabled={busy || isSelf}
                data-testid="user.is-active"
              />
            }
            label={t("userEditor.fields.isActive")}
          />
          <Typography
            variant="T3"
            sx={{ color: "text.muted", marginTop: -1.5 }}
          >
            {t(
              isSelf
                ? "userEditor.fields.isActiveSelfHelper"
                : "userEditor.fields.isActiveHelper",
            )}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={hasMembershipOverride}
                onChange={(event) =>
                  setHasMembershipOverride(event.target.checked)
                }
                disabled={busy}
                data-testid="user.membership-override"
              />
            }
            label={t("userEditor.fields.membershipOverride")}
          />
          <Typography
            variant="T3"
            sx={{ color: "text.muted", marginTop: -1.5 }}
          >
            {t("userEditor.fields.membershipOverrideHelper")}
          </Typography>
        </Paper>

        {errorKey ? (
          <Alert severity="error" data-testid="user.error">
            {t(errorKey)}
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            data-testid="user.save"
          >
            {t("userEditor.save")}
          </Button>
          {actions}
        </Box>
      </Box>

      <Paper
        component="section"
        variant="outlined"
        sx={{
          padding: 3,
          borderRadius: `${RADIUS.lg}px`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="H3" sx={{ color: "text.primary" }}>
          {t("userEditor.accountDetails")}
        </Typography>
        <DetailRow label={t("userEditor.fields.id")} value={initial.id} />
        <DetailRow
          label={t("userEditor.fields.admin")}
          value={
            <Chip
              size="small"
              variant="outlined"
              color={initial.is_admin ? "primary" : "default"}
              label={t(initial.is_admin ? "common.yes" : "common.no")}
            />
          }
        />
        <DetailRow
          label={t("userEditor.fields.marketingConsent")}
          value={t(initial.marketing_consent ? "common.yes" : "common.no")}
        />
        <DetailRow
          label={t("userEditor.fields.membershipStatus")}
          value={t(`users.membership.${initial.membership_status}`)}
        />
        <DetailRow
          label={t("userEditor.fields.membershipSyncedAt")}
          value={formatTimestamp(initial.membership_synced_at)}
        />
        <DetailRow
          label={t("userEditor.fields.overrideGrantedAt")}
          value={formatTimestamp(initial.membership_override_granted_at)}
        />
        <DetailRow
          label={t("userEditor.fields.shopifyCustomerId")}
          value={initial.shopify_customer_id ?? t("common.notSet")}
        />
        <DetailRow
          label={t("userEditor.fields.createdAt")}
          value={formatTimestamp(initial.created_at)}
        />
        <DetailRow
          label={t("userEditor.fields.updatedAt")}
          value={formatTimestamp(initial.updated_at)}
        />
        <DetailRow
          label={t("userEditor.fields.lastLoginAt")}
          value={formatTimestamp(initial.last_login_at)}
        />
      </Paper>
    </Box>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 2fr) minmax(0, 3fr)",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Typography variant="T3" sx={{ color: "text.muted" }}>
        {label}
      </Typography>
      <Typography
        component="div"
        variant="T2"
        sx={{ color: "text.secondary", overflowWrap: "anywhere" }}
      >
        {value}
      </Typography>
    </Box>
  );
}
